import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Copy, Eye, EyeOff } from 'lucide-react';
import {
  copyTextToClipboard,
  isMaskedPlaceholder,
  toastCopied,
  toastCopyFailed,
} from '../../../utils/fundingClipboardUtils';

const DEFAULT_REVEAL_MS = 45000;

function isMaskedValue(value) {
  const text = String(value || '');
  return (
    text.includes('••••')
    || text.includes('***')
    || /^\*{2,}/.test(text)
  );
}

const FundingCopyableField = ({
  label,
  value,
  displayValue,
  maskedValue,
  isSensitive = false,
  sensitive,
  canReveal = false,
  canCopy = true,
  onReveal,
  onCopy,
  copyValue,
  copyRequiresReveal = false,
  revealErrorMessage = 'Full value unavailable',
  emptyLabel = '—',
  helperText,
  className = '',
  monospace = false,
  testId,
  stopPropagation = true,
  revealAutoHideMs = DEFAULT_REVEAL_MS,
  inline = false,
}) => {
  const sensitiveMode = sensitive ?? isSensitive;
  const [revealed, setRevealed] = useState(false);
  const [revealedValue, setRevealedValue] = useState('');
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  const hasRawValue = value != null && String(value).trim() !== '';
  const hasMaskedValue = maskedValue != null && String(maskedValue).trim() !== '';
  const hasValue = sensitiveMode
    ? hasRawValue || hasMaskedValue
    : hasRawValue || (displayValue != null && String(displayValue).trim() !== '');

  const clearRevealTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearRevealTimer(), [clearRevealTimer]);

  const scheduleHide = useCallback(() => {
    clearRevealTimer();
    if (!revealAutoHideMs) return;
    timerRef.current = setTimeout(() => {
      setRevealed(false);
      setRevealedValue('');
    }, revealAutoHideMs);
  }, [clearRevealTimer, revealAutoHideMs]);

  const resolveShownValue = () => {
    if (!hasValue) return emptyLabel;
    if (sensitiveMode && !revealed) {
      return maskedValue || '••••••••';
    }
    if (revealed && revealedValue) return revealedValue;
    if (!sensitiveMode && displayValue != null && String(displayValue).trim() !== '') {
      return displayValue;
    }
    return value ?? maskedValue ?? emptyLabel;
  };

  const shown = resolveShownValue();
  const isEmpty = shown === emptyLabel;

  const resolveCopyTarget = async () => {
    if (onCopy) {
      return onCopy({ revealed, revealedValue });
    }

    if (typeof copyValue === 'function') {
      return copyValue({ revealed, revealedValue });
    }

    if (copyValue != null && copyValue !== '' && !isMaskedPlaceholder(copyValue)) {
      return String(copyValue);
    }

    if (sensitiveMode) {
      if (revealed && revealedValue) return revealedValue;
      if (copyRequiresReveal) return null;
      if (hasRawValue && !isMaskedPlaceholder(value)) return String(value);
      return null;
    }

    return String(value ?? displayValue ?? '');
  };

  const handleReveal = async (event) => {
    if (stopPropagation) event?.stopPropagation?.();
    if (!canReveal || !onReveal) return;

    if (revealed) {
      setRevealed(false);
      setRevealedValue('');
      clearRevealTimer();
      return;
    }

    setBusy(true);
    try {
      const next = await onReveal();
      if (!next || isMaskedValue(next)) {
        setRevealed(false);
        setRevealedValue('');
        clearRevealTimer();
        toastCopyFailed(revealErrorMessage);
        return;
      }
      setRevealedValue(next);
      setRevealed(true);
      scheduleHide();
    } catch (error) {
      setRevealed(false);
      setRevealedValue('');
      clearRevealTimer();
      toastCopyFailed(error?.message || revealErrorMessage);
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async (event) => {
    if (stopPropagation) event?.stopPropagation?.();
    if (!canCopy || isEmpty) return;

    setBusy(true);
    try {
      if (sensitiveMode && copyRequiresReveal && !revealed && onReveal) {
        const next = await onReveal();
        if (!next || isMaskedValue(next)) {
          setRevealed(false);
          setRevealedValue('');
          clearRevealTimer();
          toastCopyFailed(revealErrorMessage);
          return;
        }
        await copyTextToClipboard(next);
        toastCopied();
        return;
      }

      const target = await resolveCopyTarget();
      if (!target || isMaskedValue(target) || target === emptyLabel) {
        toastCopyFailed(copyRequiresReveal || sensitiveMode ? 'Reveal required before copying' : 'Full value unavailable');
        return;
      }
      if (isMaskedPlaceholder(target)) {
        toastCopyFailed('Reveal required before copying');
        return;
      }
      await copyTextToClipboard(target);
      toastCopied();
    } catch (error) {
      setRevealed(false);
      setRevealedValue('');
      clearRevealTimer();
      toastCopyFailed(error?.message || undefined);
    } finally {
      setBusy(false);
    }
  };

  const showReveal = sensitiveMode && canReveal && Boolean(onReveal);
  const showCopy = canCopy && hasValue;

  const valueClassName = [
    inline ? 'fg-copyable-field__value' : 'fg-copy-field__value',
    monospace ? 'tabular-nums font-mono' : '',
    isEmpty ? (inline ? 'text-[var(--fg-muted)]' : 'fg-copy-field__value--empty') : '',
    sensitiveMode && !isEmpty ? 'fg-copy-field__value--sensitive' : '',
    !isEmpty && !inline ? 'text-[var(--fg-text)]' : '',
    !isEmpty && inline ? 'text-[var(--fg-text)]' : '',
  ].filter(Boolean).join(' ');

  const iconButtonClass = inline ? 'fg-copyable-field__icon-btn' : 'fg-copy-field__iconButton';
  const actionsClass = inline ? 'fg-copyable-field__actions' : 'fg-copy-field__actions';

  const valueNode = (
    <span className={valueClassName} data-testid={testId}>
      {shown}
    </span>
  );

  const actions = showReveal || showCopy ? (
    <span className={actionsClass}>
      {showReveal ? (
        <button
          type="button"
          className={iconButtonClass}
          aria-label={revealed ? 'Hide value' : 'Reveal value'}
          onClick={handleReveal}
          disabled={busy}
          data-testid={testId ? `${testId}-reveal` : undefined}
        >
          {revealed ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
        </button>
      ) : null}
      {showCopy ? (
        <button
          type="button"
          className={iconButtonClass}
          aria-label={`Copy ${label || 'value'}`}
          onClick={handleCopy}
          disabled={busy}
          data-testid={testId ? `${testId}-copy` : undefined}
        >
          <Copy className="size-3.5" />
        </button>
      ) : null}
    </span>
  ) : null;

  if (inline) {
    return (
      <span className={`fg-copyable-field fg-copyable-field--inline inline-flex min-w-0 items-center gap-1.5 ${className}`}>
        {valueNode}
        {actions}
      </span>
    );
  }

  return (
    <div
      className={`fg-copy-field ${sensitiveMode ? 'fg-copy-field--secure' : ''} ${className}`}
      data-testid={testId ? `${testId}-row` : undefined}
    >
      {label ? <div className="fg-copy-field__label">{label}</div> : null}
      <div className="fg-copy-field__valueRow">
        {valueNode}
        {actions}
      </div>
      {helperText ? <div className="fg-copy-field__helper">{helperText}</div> : null}
    </div>
  );
};

export default FundingCopyableField;
